import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getBranches, getProducts, createStockTransfer } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

// A brand-new StockTransfer doesn't exist yet until the whole form is
// submitted — same client-side-only line-item builder pattern as
// AddPurchaseOrder.js: every line item lives purely in local React state
// and is sent as one array in the single POST that creates the transfer
// (see stockTransferPayload.Items on the backend).
export default function AddStockTransfer() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("stock_transfers");
  const { t: tCommon } = useTranslation("common");

  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);

  const [fromBranchId, setFromBranchId] = useState("");
  const [toBranchId, setToBranchId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);

  const [draftProductId, setDraftProductId] = useState("");
  const [draftQuantity, setDraftQuantity] = useState("");

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Defense-in-depth against a fast double-click on "Create Stock Transfer"
  // firing two near-simultaneous submits before React commits the Button
  // component's own disabled-while-loading state — see AddPurchaseOrder.js's
  // own submittingRef for the full story of why this exists.
  const submittingRef = useRef(false);

  useEffect(() => {
    getBranches(token).then(setBranches).catch(() => notify.error(t("notify_load_branches_failed", "Failed to load branches")));
    getProducts(token).then(setProducts).catch(() => notify.error(t("notify_load_products_failed", "Failed to load products")));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOptions = branches.map((b) => ({ value: String(b.id), label: b.name }));
  const productOptions = products.map((p) => ({ value: String(p.id), label: `${p.name} (${p.sku})` }));

  const addItem = () => {
    if (!draftProductId) {
      notify.error(t("validation_item_product_required", "Pick a product for every line item"));
      return;
    }
    const qty = Number(draftQuantity);
    if (!qty || qty <= 0) {
      notify.error(t("validation_item_quantity_required", "Every line item needs a quantity greater than zero"));
      return;
    }
    const product = products.find((p) => String(p.id) === draftProductId);

    setItems((prev) => [
      ...prev,
      {
        key: `${draftProductId}-${Date.now()}`,
        product_id: Number(draftProductId),
        product_name: product?.name || "",
        product_sku: product?.sku || "",
        quantity: qty,
      },
    ]);
    setDraftProductId("");
    setDraftQuantity("");
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const handleSubmit = async () => {
    if (submittingRef.current) return;

    setErrors({});
    const newErrors = {};
    if (!fromBranchId) newErrors.fromBranch = t("validation_from_branch_required", "Source branch is required");
    if (!toBranchId) newErrors.toBranch = t("validation_to_branch_required", "Destination branch is required");
    if (fromBranchId && toBranchId && fromBranchId === toBranchId) {
      newErrors.toBranch = t("validation_branches_must_differ", "Source and destination branches must be different");
    }
    if (items.length === 0) newErrors.items = t("validation_items_required", "Add at least one line item");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    const payload = {
      from_branch_id: Number(fromBranchId),
      to_branch_id: Number(toBranchId),
      notes: notes.trim(),
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
    };

    try {
      const created = await createStockTransfer(token, payload);
      notify.success(t("notify_created", "Stock transfer created successfully"));
      navigate(`/stock-transfers/${created.id}`);
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/stock-transfers")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{t("add_title", "New Stock Transfer")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("add_subtitle", "Pick a source and destination branch, then add the products you're moving")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectBox label={t("field_from_branch_label", "From Branch")} options={branchOptions} value={fromBranchId} onChange={setFromBranchId} error={errors.fromBranch} icon={ICONS.Branches} />
            <SelectBox label={t("field_to_branch_label", "To Branch")} options={branchOptions} value={toBranchId} onChange={setToBranchId} error={errors.toBranch} icon={ICONS.Branches} />
          </div>

          <FloatingInput label={t("field_notes_label", "Notes (optional)")} value={notes} onChange={(e) => setNotes(e.target.value)} />

          <div className="pt-4 border-t border-gray-50">
            <h3 className="text-sm font-bold text-gray-700 mb-3">{t("items_section_title", "Line Items")}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-3 items-end mb-4">
              <SelectBox label={t("field_product_label", "Product")} options={productOptions} value={draftProductId} onChange={setDraftProductId} />
              <FloatingInput label={t("field_quantity_label", "Quantity")} type="number" min="0" value={draftQuantity} onChange={(e) => setDraftQuantity(e.target.value)} />
              <Button variant="secondary" onClick={addItem} className="h-[56px]">{t("btn_add_item", "+ Add item")}</Button>
            </div>

            {errors.items && <p className="text-[10px] text-red-500 font-bold mb-3 ms-2 uppercase tracking-tight">{errors.items}</p>}

            {items.length === 0 ? (
              <p className="text-gray-400 text-sm italic">{t("items_empty", "No line items yet — add at least one product above.")}</p>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <div key={item.key} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-4 py-3">
                    <div className="flex-grow">
                      <p className="font-bold text-gray-800 text-sm">{item.product_name}</p>
                      <p className="text-xs text-gray-400 font-mono">{item.product_sku}</p>
                    </div>
                    <div className="text-xs text-gray-500 font-mono">{item.quantity}</div>
                    <button type="button" onClick={() => removeItem(item.key)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                      <ICONS.Close className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={ICONS.Plus}
            >
              {t("btn_create_stock_transfer", "Create Stock Transfer")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/stock-transfers")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
